/**
 * spatialFocusArbitrator.js
 *
 * The ONE place that turns a server spatial-focus receipt into a render plan. It is pure: no Vuex,
 * no Forge, no DOM, no clock. `useSpatialFocus` resolves, this decides, the Forge adapter paints.
 *
 * Replaces the per-caller precedence logic that used to live in the dashboard POI builders, the
 * Task paint planner and the anomaly paint special case - each of which collapsed claims at its
 * own grain (tag, target, bare dbId) and let iteration order pick the winning colour.
 *
 * The invariants it owns (`.codex/features/spatial-focus/README.md` - "Arbitration and rendering"):
 *
 *   - Geometry identity is (normalized model URN, dbId). A bare dbId is never an identity, so the
 *     same dbId in two loaded models stays two claims.
 *   - Context claims start gray, and gray is emitted only when a visible target actually binds.
 *   - A target outranks context gray on the same identity.
 *   - Same final style coalesces; ANY differing style becomes MIXED. No category, numeric, range
 *     or array order is read as a severity ranking.
 *   - An URN-less element bucket binds only when exactly one model is loaded; otherwise it reports
 *     `ambiguousModel` rather than guessing. A known URN that is not loaded reports `modelMismatch`
 *     and blocks only its own address.
 *   - Everything is order-independent: permuting occurrences, subjects, addresses or loaded models
 *     produces a deep-equal plan. Every array the plan returns is sorted by a stable key.
 *
 * Gate:      `.codex/tests/spatial-focus-arbitrator.assert.cjs`
 * Contracts: aiva.api `MODS.Forge.Api/Services/SpatialFocus/SpatialFocusContracts.cs`. The wire is
 *            camelCase (Startup.cs:97 `AddNewtonsoftJson`, whose default resolver is camelCase) and
 *            `Vector3` arrives as `{ x, y, z }`.
 */

import { GHOST_COLOR } from '@/features/Tags/components/monitoring/cmlColoringConstants';

/**
 * The one non-severity conflict colour. Mirrors `--ds-color-mixed` in
 * `src/styles/_design-tokens.scss`; exported so no caller redeclares the hex.
 */
export const MIXED_COLOR = '#665B9B';

/** Context gray reuses the existing ghost tone rather than introducing a second neutral. */
export const CONTEXT_COLOR = GHOST_COLOR;

const ROLE_TARGET = 'target';
const ROLE_CONTEXT = 'context';

const STATE_TARGET = 'target';
const STATE_CONTEXT = 'context';
const STATE_MIXED = 'mixed';

const KIND_POINT = 'point';

// Subject-state diagnostics reuse the SERVER's spellings (SpatialFocusDiagnosticCodes) so the
// legend never has to translate between two vocabularies. The last three are decisions only the
// client can make, because only the client knows which models are loaded and what it painted.
const DIAGNOSTIC_SUBJECT_MISSING = 'subjectMissing';
const DIAGNOSTIC_UNSUPPORTED_TARGET = 'unsupportedTarget';
const DIAGNOSTIC_ADDRESS_CAP = 'addressCap';
const DIAGNOSTIC_AMBIGUOUS_MODEL = 'ambiguousModel';
const DIAGNOSTIC_MODEL_MISMATCH = 'modelMismatch';
const DIAGNOSTIC_CONTEXT_WITHOUT_TARGET = 'contextWithoutTarget';
const DIAGNOSTIC_MIXED_STYLE = 'mixedStyle';

/** A binding's own state, mapped onto the server diagnostic that describes it. */
const SUBJECT_STATE_DIAGNOSTICS = Object.freeze({
  missing: DIAGNOSTIC_SUBJECT_MISSING,
  unsupported: DIAGNOSTIC_UNSUPPORTED_TARGET,
  capped: DIAGNOSTIC_ADDRESS_CAP,
});

/** NUL cannot appear in a URN, so it cannot forge a collision between two identities. */
const IDENTITY_SEPARATOR = '\u0000';

/** An uncoloured target is a STYLE, not the absence of one, so it needs a key of its own. */
const NO_COLOUR_KEY = `${IDENTITY_SEPARATOR}none`;

/**
 * The repository's Forge URN comparison rule, stated once.
 *
 * `forgeService.js:2119-2121` (and `nodeControls.vue:591-593`, `smallModelRow.vue:165-167`) match a
 * viewer seed URN against a stored URN spelled with '', '=' or '==' padding. The server states the
 * same rule in `SpatialFocusHydration.cs:609-616`: trim whitespace, strip trailing '=', PRESERVE
 * case - two spellings of one model must share a bucket, and two models whose ids differ only by
 * case must not be merged into one.
 *
 * @param {string|null|undefined} urn
 * @returns {string|null} the normalized URN, or null when there is none
 */
export function normalizeModelUrn(urn) {
  if (typeof urn !== 'string') return null;
  const trimmed = urn.trim().replace(/=+$/, '');
  return trimmed.length ? trimmed : null;
}

/** Colours compare and render in one canonical spelling, so '#FF0000' never fakes a conflict. */
function normalizeColour(colour) {
  if (typeof colour !== 'string') return null;
  const trimmed = colour.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

/**
 * A finite number, or null. `Number()` alone is not enough at a boundary: it turns null, '' and
 * false into 0, which would fabricate element 0 and place a marker at the world origin.
 *
 * It is also what canonicalizes a dbId, so `'42'` and `42` are one identity rather than two.
 */
function toNumber(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * A marker needs three finite coordinates. A malformed point yields null rather than a default,
 * because a marker silently placed at the world origin is worse than a marker that never appears
 * and says so. The server guards this too (`SpatialFocusHydration.cs:287`), but this module is the
 * boundary, and a boundary does not inherit an upstream guarantee.
 */
function toPoint(point) {
  if (!point || typeof point !== 'object') return null;
  const x = toNumber(point.x);
  const y = toNumber(point.y);
  const z = toNumber(point.z);
  if (x === null || y === null || z === null) return null;
  return { x, y, z };
}

function compareText(left, right) {
  const a = left === null || left === undefined ? '' : String(left);
  const b = right === null || right === undefined ? '' : String(right);
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function sortedKeys(keys) {
  return [...keys].sort(compareText);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Index the loaded viewer models by normalized URN. `model` is an opaque handle the plan hands
 * straight back to the Forge adapter; the arbitrator never calls a method on it, which is what
 * keeps this module pure and directly testable.
 */
function indexLoadedModels(loadedModels) {
  const byUrn = new Map();
  const entries = [];

  asArray(loadedModels).forEach((entry) => {
    if (!entry) return;
    const isText = typeof entry === 'string';
    const urn = normalizeModelUrn(isText ? entry : (entry.urn || entry.modelUrn));
    const resolved = { urn, model: isText ? null : (entry.model === undefined ? null : entry.model) };
    entries.push(resolved);
    if (urn !== null && !byUrn.has(urn)) byUrn.set(urn, resolved);
  });

  return { byUrn, sole: entries.length === 1 ? entries[0] : null };
}

/**
 * Resolve one server address against the loaded models.
 *
 * ELEMENT addresses need a model: a dbId is only meaningful inside one. A bucket the server left
 * URN-less binds to the single loaded model and adopts its URN - so it coalesces with an explicitly
 * addressed claim on the same geometry - and ONLY then: with several or zero models loaded there is
 * no honest choice, so it reports `ambiguousModel` instead of guessing.
 *
 * POINT addresses are global-frame coordinates and the server never stamps a URN on one
 * (`SpatialFocusHydration.cs:290-298`), so they render without a model. A point that does carry a
 * URN is still checked, so a future model-scoped point cannot slip past the mismatch rule.
 */
function bindAddress(address, models) {
  const urn = normalizeModelUrn(address.modelUrn);

  if (urn !== null) {
    const loaded = models.byUrn.get(urn);
    return loaded
      ? { modelUrn: loaded.urn, model: loaded.model, blocked: null }
      : { modelUrn: null, model: null, blocked: DIAGNOSTIC_MODEL_MISMATCH };
  }

  if (address.kind === KIND_POINT) return { modelUrn: null, model: null, blocked: null };
  if (models.sole) return { modelUrn: models.sole.urn, model: models.sole.model, blocked: null };

  return { modelUrn: null, model: null, blocked: DIAGNOSTIC_AMBIGUOUS_MODEL };
}

/** Group finished claims into the model-scoped buckets the Forge adapter fits and pulses. */
function bucketByModel(claims) {
  const buckets = new Map();

  claims.forEach((claim) => {
    const key = claim.modelUrn === null ? '' : claim.modelUrn;
    if (!buckets.has(key)) {
      buckets.set(key, { model: claim.model, modelUrn: claim.modelUrn, dbIds: [] });
    }
    buckets.get(key).dbIds.push(claim.dbId);
  });

  return [...buckets.values()]
    .map((bucket) => ({ ...bucket, dbIds: bucket.dbIds.slice().sort((a, b) => a - b) }))
    .sort((a, b) => compareText(a.modelUrn, b.modelUrn));
}

/**
 * Arbitrate one server receipt into a deterministic render plan.
 *
 * @param {object} input
 * @param {Array}  input.occurrences  ordered, uncollapsed server occurrences
 * @param {Array}  input.subjects     stable `{ subjectKey, kind, id, state }` subjects
 * @param {Array}  input.addresses    model-scoped `{ addressKey, kind, modelUrn, dbIds, point }`
 * @param {{column?:string, displayName?:string, colorFor?:(value:*)=>string}} [input.encoding]
 *        `colorFor` is the widget's live value->hex resolver (see `utils/isolateColor.js`). With no
 *        resolver the targets stay uncoloured - a plain isolate - rather than inventing a hue.
 * @param {Array<{urn:string, model:*}>} [input.loadedModels] models currently in the viewer
 * @param {string} [input.mixedColor] conflict colour; defaults to {@link MIXED_COLOR}
 * @returns {{
 *   elementClaims: Array<{model:*, modelUrn:string|null, dbId:number, color:string|null,
 *                         state:'target'|'context'|'mixed', subjectKeys:string[]}>,
 *   markers: Array<{addressKey:string, subjectKeys:string[], modelUrn:string|null, model:*,
 *                   point:{x:number,y:number,z:number}, positioned:boolean, color:string|null,
 *                   state:'target'|'mixed'}>,
 *   fitTargets: {role:'target'|'context'|null,
 *                elements:Array<{model:*, modelUrn:string|null, dbIds:number[]}>,
 *                points:Array<{x:number,y:number,z:number}>},
 *   pulseTargets: Array<{model:*, modelUrn:string|null, dbIds:number[]}>,
 *   diagnostics: Array<{code:string, count:number}>,
 *   counts: {records:number, occurrences:number, targets:number, geometryObjects:number,
 *            elements:number, markers:number, mixed:number, unresolved:number},
 *   partial: boolean
 * }}
 */
export function buildSpatialRenderPlan({
  occurrences,
  subjects,
  addresses,
  encoding,
  loadedModels,
  mixedColor = MIXED_COLOR,
} = {}) {
  const models = indexLoadedModels(loadedModels);
  const colourFor = encoding && typeof encoding.colorFor === 'function'
    ? encoding.colorFor
    : () => null;

  // Subjects supply a binding's state when the binding itself omits one. They are NOT used to
  // re-derive renderability: whether a claim can paint is decided by whether its ADDRESS bound to
  // a loaded model, which is a fact about the viewer rather than about the domain.
  const subjectStateByKey = new Map();
  asArray(subjects).forEach((subject) => {
    if (subject && subject.subjectKey) subjectStateByKey.set(subject.subjectKey, subject.state);
  });

  // Each address is bound to a model exactly once, so the cost stays linear and a blocked address
  // is reported once no matter how many rows claimed it.
  const addressByKey = new Map();
  const bindingByKey = new Map();
  asArray(addresses).forEach((address) => {
    if (!address || !address.addressKey) return;
    addressByKey.set(address.addressKey, address);
    bindingByKey.set(address.addressKey, bindAddress(address, models));
  });

  const identities = new Map();      // (URN, dbId) -> element claim accumulator
  const markerCandidates = new Map(); // addressKey -> point marker accumulator
  const blockedAddresses = new Map(); // addressKey -> diagnostic code (deduped per address)
  const diagnosticCounts = new Map();
  const rowKeys = new Set();

  let unkeyedRecords = 0;
  let occurrenceCount = 0;
  let unresolvedOccurrences = 0;
  let contextBindingCandidates = 0;

  const addDiagnostic = (code, amount = 1) => {
    diagnosticCounts.set(code, (diagnosticCounts.get(code) || 0) + amount);
  };

  const identityFor = (bound, dbId) => {
    const key = `${bound.modelUrn === null ? '' : bound.modelUrn}${IDENTITY_SEPARATOR}${dbId}`;
    if (!identities.has(key)) {
      identities.set(key, {
        model: bound.model,
        modelUrn: bound.modelUrn,
        dbId,
        targetColours: new Map(),
        targetSubjects: new Set(),
        contextSubjects: new Set(),
      });
    }
    return identities.get(key);
  };

  const markerFor = (address, bound, point) => {
    if (!markerCandidates.has(address.addressKey)) {
      markerCandidates.set(address.addressKey, {
        addressKey: address.addressKey,
        model: bound.model,
        modelUrn: bound.modelUrn,
        point,
        positioned: address.positioned === true,
        targetColours: new Map(),
        targetSubjects: new Set(),
      });
    }
    return markerCandidates.get(address.addressKey);
  };

  asArray(occurrences).forEach((occurrence) => {
    if (!occurrence || typeof occurrence !== 'object') return;
    occurrenceCount += 1;

    const rowKey = typeof occurrence.rowKey === 'string' && occurrence.rowKey.length
      ? occurrence.rowKey
      : null;
    if (rowKey) rowKeys.add(rowKey);
    else unkeyedRecords += 1;

    // One colour lookup per ROW: every target that row claims paints the row's encoded value.
    const rowColour = normalizeColour(colourFor(occurrence.encodingValue));
    const rowColourKey = rowColour === null ? NO_COLOUR_KEY : rowColour;

    // A row is "resolved" only when its TARGET reached the screen. A row whose context grayed while
    // its own point of interest was blocked has still lost the thing it was about, and the legend
    // has to say so - a blank viewer reported as "0 unresolved" is exactly the silent outcome the
    // typed-outcome invariant exists to prevent.
    let renderedTarget = false;

    asArray(occurrence.bindings).forEach((binding) => {
      if (!binding) return;
      const isTarget = binding.role === ROLE_TARGET;
      // A role this client does not understand is not honoured as a claim: guessing which of gray
      // and colour it meant is exactly the silent decision this module exists to remove.
      if (!isTarget && binding.role !== ROLE_CONTEXT) return;

      const subjectKey = typeof binding.subjectKey === 'string' && binding.subjectKey.length
        ? binding.subjectKey
        : null;

      // Deduped per binding: a state of 'unsupported' and an explicit 'unsupportedTarget' code are
      // one fact about one claim, not two problems.
      const codes = new Set(asArray(binding.diagnosticCodes).filter((code) => typeof code === 'string' && code));
      const state = binding.state || (subjectKey ? subjectStateByKey.get(subjectKey) : null);
      const stateCode = SUBJECT_STATE_DIAGNOSTICS[state];
      if (stateCode) codes.add(stateCode);
      codes.forEach((code) => addDiagnostic(code));

      let claimedContext = false;

      asArray(binding.addressKeys).forEach((addressKey) => {
        const address = addressByKey.get(addressKey);
        if (!address) return;

        const bound = bindingByKey.get(addressKey);
        if (bound.blocked) {
          blockedAddresses.set(addressKey, bound.blocked);
          return;
        }

        if (address.kind === KIND_POINT) {
          // Context has no marker channel: a gray point would claim a highlight it is not.
          if (!isTarget) return;
          const point = toPoint(address.point);
          if (point === null) return;

          const marker = markerFor(address, bound, point);
          marker.targetColours.set(rowColourKey, rowColour);
          if (subjectKey) marker.targetSubjects.add(subjectKey);
          renderedTarget = true;
          return;
        }

        asArray(address.dbIds).forEach((rawDbId) => {
          const dbId = toNumber(rawDbId);
          if (dbId === null) return;

          const identity = identityFor(bound, dbId);
          if (isTarget) {
            identity.targetColours.set(rowColourKey, rowColour);
            if (subjectKey) identity.targetSubjects.add(subjectKey);
            renderedTarget = true;
          } else {
            if (subjectKey) identity.contextSubjects.add(subjectKey);
            claimedContext = true;
          }
        });
      });

      // Only a context that actually produced an identity can be suppressed by the gate. Counting
      // every context BINDING would report a suppression for a context that had nothing to show.
      if (claimedContext) contextBindingCandidates += 1;
    });

    if (!renderedTarget) unresolvedOccurrences += 1;
  });

  blockedAddresses.forEach((code) => addDiagnostic(code));

  // Invariant 6: gray explains a highlight. With no target actually bound to a loaded model there
  // is no highlight to explain, so the scene stays untouched and says why instead of graying the
  // whole asset for something that can never appear.
  const hasVisibleTarget = markerCandidates.size > 0
    || [...identities.values()].some((identity) => identity.targetColours.size > 0);

  const renderedTargetSubjects = new Set();
  const elementClaims = [];
  let mixedIdentities = 0;

  identities.forEach((identity) => {
    if (identity.targetColours.size > 0) {
      const isMixed = identity.targetColours.size > 1;
      if (isMixed) mixedIdentities += 1;
      identity.targetSubjects.forEach((key) => renderedTargetSubjects.add(key));
      elementClaims.push({
        model: identity.model,
        modelUrn: identity.modelUrn,
        dbId: identity.dbId,
        color: isMixed ? mixedColor : [...identity.targetColours.values()][0],
        state: isMixed ? STATE_MIXED : STATE_TARGET,
        subjectKeys: sortedKeys(identity.targetSubjects),
      });
      return;
    }

    if (identity.contextSubjects.size === 0 || !hasVisibleTarget) return;

    elementClaims.push({
      model: identity.model,
      modelUrn: identity.modelUrn,
      dbId: identity.dbId,
      color: CONTEXT_COLOR,
      state: STATE_CONTEXT,
      subjectKeys: sortedKeys(identity.contextSubjects),
    });
  });

  elementClaims.sort((a, b) => compareText(a.modelUrn, b.modelUrn) || (a.dbId - b.dbId));

  const markers = [...markerCandidates.values()]
    .map((candidate) => {
      const isMixed = candidate.targetColours.size > 1;
      if (isMixed) mixedIdentities += 1;
      candidate.targetSubjects.forEach((key) => renderedTargetSubjects.add(key));
      return {
        addressKey: candidate.addressKey,
        subjectKeys: sortedKeys(candidate.targetSubjects),
        model: candidate.model,
        modelUrn: candidate.modelUrn,
        point: candidate.point,
        positioned: candidate.positioned,
        color: isMixed ? mixedColor : [...candidate.targetColours.values()][0],
        state: isMixed ? STATE_MIXED : STATE_TARGET,
      };
    })
    .sort((a, b) => compareText(a.addressKey, b.addressKey));

  if (!hasVisibleTarget && contextBindingCandidates > 0) {
    addDiagnostic(DIAGNOSTIC_CONTEXT_WITHOUT_TARGET, contextBindingCandidates);
  }
  if (mixedIdentities > 0) addDiagnostic(DIAGNOSTIC_MIXED_STYLE, mixedIdentities);

  const targetClaims = elementClaims.filter((claim) => claim.state !== STATE_CONTEXT);
  const contextClaims = elementClaims.filter((claim) => claim.state === STATE_CONTEXT);
  const pulseTargets = bucketByModel(targetClaims);

  // Fit targets first. Context is the fallback only when nothing targeted rendered at all.
  let fitTargets = { role: null, elements: [], points: [] };
  if (targetClaims.length > 0 || markers.length > 0) {
    fitTargets = {
      role: ROLE_TARGET,
      elements: pulseTargets,
      points: markers.map((marker) => marker.point),
    };
  } else if (contextClaims.length > 0) {
    fitTargets = { role: ROLE_CONTEXT, elements: bucketByModel(contextClaims), points: [] };
  }

  const diagnostics = [...diagnosticCounts.entries()]
    .filter(([, count]) => count > 0)
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => compareText(a.code, b.code));

  return {
    elementClaims,
    markers,
    fitTargets,
    pulseTargets,
    diagnostics,
    counts: {
      records: rowKeys.size + unkeyedRecords,
      occurrences: occurrenceCount,
      targets: renderedTargetSubjects.size,
      // Deliberately NOT called `addresses`: the server's `counts.addresses` is per ADDRESS, and
      // one address can carry fifty dbIds. This is what actually reached the screen, per painted
      // element and per marker - the legend contract's `geometryObjects`.
      geometryObjects: elementClaims.length + markers.length,
      elements: elementClaims.length,
      markers: markers.length,
      mixed: mixedIdentities,
      unresolved: unresolvedOccurrences,
    },
    // MIXED rendered everything it was given, so it is a conflict - not a loss. Every other
    // diagnostic means a claim the caller made did not reach the screen.
    partial: unresolvedOccurrences > 0
      || diagnostics.some((entry) => entry.code !== DIAGNOSTIC_MIXED_STYLE),
  };
}
